'use client';
import { CardItemBlockEntity, CardOfferEntity } from '@entities/desktop/cards';
import {
  CardOfferEntityItem,
  CardOfferEntityItemInfo,
} from '@entities/desktop/cards/card-offer/ui/card-offer';
import { AddToFavoriteItemCardFeature } from '@features/desktop/pet/item-card/add-to-favorite/ui/add-to-favorite';
import { useDragToScroll } from '@hooks/client';
import {
  ItemCardWidgetBreadcrumbsProps,
  ItemCardWidgetDescriptionProps,
  ItemCardWidgetItemAgeProps,
  ItemCardWidgetItemPropertiesProps,
  ItemCardWidgetItemProps,
  ItemCardWidgetItemTitleProps,
  ItemCardWidgetProps,
} from '@widgets/desktop/item-card/ui/item-card.types';
import cn from 'clsx';
import { observer } from 'mobx-react-lite';
import { motion } from 'motion/react';
import Link from 'next/link';
import { notFound, usePathname, useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';

import { ALL_FILTERS } from '@/mocks/filters';
import { commonAges, neonAges } from '@/mocks/petsAges';
import { useGetProductsRealName } from '@/shared/api/product/generated/🧸-товары/🧸-товары';
import { useDescription } from '@/shared/hooks/useDescription';
import { GlobalItemGroups, ItemAge, Product } from '@/shared/types/common';
import { Illustration, Skeleton, TagInfo } from '@/shared/ui';
import { Breadcrumbs } from '@/shared/ui/desktop/breadcrumbs';
import {
  BreadcrumbsItem,
  BreadcrumbsItemSeparator,
} from '@/shared/ui/desktop/breadcrumbs/breadcrumbs';
import { Tabs, TabsItem } from '@/shared/ui/tabs';
import { TagInfoVariant } from '@/shared/ui/tag-info/tag-info.types';
import { Typography } from '@/shared/ui/typography';
import { useStore } from '@/store/context';

import styles from './item-card.module.scss';

export const ItemCardWidget = ({
  className,
  category,
  productId,
  pet,
  ...props
}: ItemCardWidgetProps) => {
  const itemCardClasses = cn(styles['item-card'], className);

  const [item, setItem] = useState<GlobalItemGroups | undefined>(undefined);
  const [product, setProduct] = useState<Product | undefined>(undefined);
  const [localProductId, setLocalProductId] = useState<number>(productId);
  const [dataProcessed, setDataProcessed] = useState<boolean>(false);

  const [age, setAge] = useState(product?.age);
  const [type, setType] = useState(product?.pumping);
  const [flyable, setFlyable] = useState(product?.flyable);
  const [rideable, setRideable] = useState(product?.rideable);

  const {
    data: productData,
    isLoading,
    error,
    isSuccess,
    isFetching,
    refetch,
  } = useGetProductsRealName(pet);

  const loadProductInBackground = async (newProductId: number) => {
    setLocalProductId(newProductId);
    //refetch();
  };

  const handlePropertieChange = (
    item: GlobalItemGroups,
    query: Partial<Product>,
  ) => {
    if (
      !item ||
      !item.products ||
      !Array.isArray(item.products) ||
      item.products.length === 0
    ) {
      return;
    }

    const scoredProducts = item.products
      .map(product => {
        let score = 0;
        if (product.pumping === query.pumping) score += 3;
        if (product.flyable === query.flyable) score += 2;
        if (product.rideable === query.rideable) score += 2;
        if (product.age === query.age) score += 1;

        return { product, score };
      })
      .sort((a, b) => b.score - a.score);

    const bestMatch =
      scoredProducts.length > 0 ? scoredProducts[0].product : null;

    if (bestMatch) {
      const newUrl = `/en/shop/${item.type}/${item.realName}/${bestMatch.id}`;
      window.history.replaceState({}, '', newUrl);
      loadProductInBackground(Number(bestMatch.id));
      setProduct(bestMatch);
    } else {
      //console.warn('No product matches:', query, 'Available products:', item.products.length);
    }
  };

  useEffect(() => {
    if (product) {
      const query = {
        flyable: flyable,
        rideable: rideable,
        age: age,
        pumping: type,
      };

      product && item && handlePropertieChange(item, query);
    }
  }, [flyable, rideable, type, age]);

  useEffect(() => {
    if (productData?.data) {
      try {
        const itemFromApi = productData?.data as unknown as GlobalItemGroups;
        setItem(itemFromApi);

        if (
          itemFromApi &&
          itemFromApi.products &&
          Array.isArray(itemFromApi.products)
        ) {
          const foundProduct = itemFromApi.products.find(
            p => Number(p.id) === Number(localProductId),
          );
          setProduct(foundProduct);
          setAge(foundProduct?.age);
          setType(foundProduct?.pumping);
          setFlyable(foundProduct?.flyable);
          setRideable(foundProduct?.rideable);
        }

        setDataProcessed(true);
      } catch (e) {
        setItem(undefined);
        setProduct(undefined);
        setDataProcessed(true);
      }
    }
  }, [productData, localProductId]);

  if (isLoading) {
    return (
      <div className={itemCardClasses} {...props}>
        <Skeleton className={styles['skeleton--breadcrumbs']} />

        <div className={styles['content']}>
          <div className={styles['left']}>
            <Skeleton className={styles['skeleton--item']} />
            <Skeleton className={styles['skeleton--btn']} />
          </div>
          <div className={styles['right']}>
            <div className={styles['title-info']}>
              <Skeleton className={styles['skeleton--info']} />
            </div>
            <Skeleton className={styles['skeleton--prop']} />
            <Skeleton className={styles['skeleton--prop']} />
            <Skeleton className={styles['skeleton--prop']} />
            {/*<ItemCardWidget.Bundles product={product} />*/}
            <div className={styles['descr']}>
              <Skeleton className={styles['skeleton--descr']} />
            </div>
          </div>
        </div>

        <div className={styles['descr-content']}>
          <Skeleton />
        </div>
      </div>
    );
  }

  if (!item && isSuccess && !isFetching && dataProcessed) {
    return notFound();
  }

  return (
    <div className={itemCardClasses} {...props}>
      <ItemCardWidget.Breadcrumbs item={item} />

      <div className={styles['content']}>
        <div className={styles['left']}>
          <ItemCardWidget.Item item={item} />
          <AddToFavoriteItemCardFeature realName={item?.realName!} />
        </div>
        <div className={styles['right']}>
          <div className={styles['title-info']}>
            <ItemCardWidget.ItemTitle name={item?.name} />
            <ItemCardWidget.ItemTags rare={item?.rare ?? 'common'} />
          </div>
          <ItemCardWidget.ItemType
            item={item}
            product={product}
            handlePropertiesChange={type => setType(type)}
          />
          <ItemCardWidget.Options
            item={item}
            handlePropertiesChange={type => setType(type)}
          />
          <ItemCardWidget.ItemProperties
            item={item}
            product={product}
            handleFlyableChange={value => setFlyable(value)}
            handleRideableChange={value => setRideable(value)}
          />
          <ItemCardWidget.ItemAge
            item={item}
            product={product}
            handlePropertiesChange={age => setAge(age)}
          />
          {/*<ItemCardWidget.Bundles product={product} />*/}
          {item?.realName && (
            <div className={styles['descr']}>
              <ItemCardWidget.Description realName={item?.realName} />
            </div>
          )}
        </div>
      </div>

      {item?.realName && (
        <div className={styles['descr-content']}>
          <ItemCardWidget.Description realName={item?.realName} />
        </div>
      )}
    </div>
  );
};

ItemCardWidget.ItemTitle = observer(
  ({ name }: ItemCardWidgetItemTitleProps) => {
    return (
      <Typography className={styles['title']} weight={'semi-bold'}>
        {name}
      </Typography>
    );
  },
);

ItemCardWidget.ItemTags = observer(({ rare }: { rare: TagInfoVariant }) => {
  const tags: { variant: TagInfoVariant; text: string }[] = [
    { variant: 'discount', text: '-30%' },
    { variant: 'hot-price', text: 'Hot Price' },
  ];

  return (
    <div className={styles['tags']}>
      <TagInfo
        key={`card-item-${tags[1]?.variant}-${1}`}
        className={styles['tag-info']}
        variant={tags[1]?.variant}
      >
        {tags[1]?.text}
      </TagInfo>
      <TagInfo className={styles['tag-info']} variant={rare}>
        {rare}
      </TagInfo>
      <TagInfo
        key={`card-item-${tags[0]?.variant}-${0}`}
        className={styles['tag-info']}
        variant={tags[0]?.variant}
      >
        {tags[0]?.text}
      </TagInfo>
    </div>
  );
});

ItemCardWidget.ItemType = observer(
  ({
    item,
    product,
    handlePropertiesChange,
  }: {
    item?: GlobalItemGroups;
    product?: Product;
    handlePropertiesChange: (tab: any) => void;
  }) => {
    const game = useStore()?.app?.selectedGame;

    if (!item || !product || game === 'mm2' || item.type !== 'pet') return null;

    const defaultActiveTab = product.pumping;

    return (
      <div className={styles['properties']}>
        <Typography className={styles['title']}>Item type</Typography>
        <Tabs<'neon' | 'mega_neon' | 'default'>
          className={styles['tabs-container']}
          defaultActiveTab={defaultActiveTab!}
          isSelectable
          onTabsChange={tab => handlePropertiesChange(tab)}
        >
          <TabsItem
            className={styles['tab-item']}
            tagCategory={'default'}
            variant={'primary'}
            value={'default'}
          >
            Common
          </TabsItem>
          <TabsItem
            className={styles['tab-item']}
            tagCategory={'neon'}
            variant={'primary'}
            value={'neon'}
          >
            Neon
          </TabsItem>
          <TabsItem
            className={styles['tab-item']}
            tagCategory={'mega_neon'}
            variant={'primary'}
            value={'mega_neon'}
          >
            Mega-Neon
          </TabsItem>
        </Tabs>
      </div>
    );
  },
);

ItemCardWidget.ItemProperties = observer(
  ({
    item,
    product,
    handleRideableChange,
    handleFlyableChange,
  }: ItemCardWidgetItemPropertiesProps) => {
    if (!item || !product || item?.type === 'transport' || item?.type === 'egg')
      return null;

    const game = useStore().app.selectedGame;

    const hasChromaSet = true;
    const router = useRouter();

    return (
      <div className={styles['properties']}>
        <Typography className={styles['title']}>Properties</Typography>
        {game === 'adopt' && item.type === 'pet' && (
          <div className={styles['tabs-container']}>
            <TabsItem
              className={styles['tab-item']}
              tagCategory={'fly'}
              variant={'primary'}
              onClick={() => handleFlyableChange(!Boolean(product?.flyable))}
              isSelected={Boolean(product.flyable)}
              value={'fly'}
            >
              Flyable
            </TabsItem>
            <TabsItem
              className={styles['tab-item']}
              tagCategory={'ride'}
              variant={'primary'}
              onClick={() => handleRideableChange(!Boolean(product?.rideable))}
              isSelected={Boolean(product?.rideable)}
              value={'ride'}
            >
              Rideable
            </TabsItem>
          </div>
        )}
        {game === 'mm2' && (
          <Tabs
            className={styles['tabs-container']}
            defaultActiveTab={item.type}
            isSelectable
            onTabsChange={() => {}}
          >
            <TabsItem
              className={styles['tab-item']}
              variant={'quaternary'}
              disabled={false}
              tagCategory={'default'}
              value={'default'}
            >
              Regular
            </TabsItem>
            <TabsItem
              tagCategory={'chroma'}
              className={styles['tab-item']}
              variant={'quaternary'}
              disabled={!hasChromaSet && !item.type}
              value={'chroma'}
            >
              Chroma
            </TabsItem>
          </Tabs>
        )}
      </div>
    );
  },
);

ItemCardWidget.ItemAge = observer(
  ({ item, product, handlePropertiesChange }: ItemCardWidgetItemAgeProps) => {
    const game = useStore()?.app?.selectedGame;
    if (
      !item ||
      !product ||
      item.type !== 'pet' ||
      product.pumping === 'mega_neon' ||
      game === 'mm2'
    )
      return;

    return (
      <div className={styles['age']}>
        <Typography className={styles['title']}>Age</Typography>
        <Tabs<ItemAge>
          className={styles['tabs-container']}
          isSelectable
          onTabsChange={value => {
            handlePropertiesChange(value);
          }}
        >
          {(product.pumping === 'default' ? commonAges : neonAges).map(age => (
            <TabsItem
              key={age.value}
              className={styles['tab-item']}
              variant={'quaternary'}
              isSelected={product?.age === age.value}
              value={age.value}
            >
              {age.label}
            </TabsItem>
          ))}
        </Tabs>
      </div>
    );
  },
);

ItemCardWidget.Options = observer(
  ({ item, product, handlePropertiesChange }: ItemCardWidgetItemAgeProps) => {
    const game = useStore()?.app?.selectedGame;

    const hasChromaSet = true;
    const router = useRouter();

    if (!item || game === 'adopt') return;

    return (
      <div className={styles['age']}>
        <Typography className={styles['title']}>Options</Typography>
        {game === 'mm2' && (
          <Tabs
            className={styles['tabs-container']}
            defaultActiveTab={item.type}
            isSelectable
            onTabsChange={() => {}}
          >
            <TabsItem
              className={styles['tab-item']}
              variant={'quaternary'}
              disabled={!hasChromaSet && !item.type}
              tagCategory={'star-circle'}
              value={'single'}
            >
              Single
            </TabsItem>

            <TabsItem
              className={styles['tab-item']}
              variant={'quaternary'}
              disabled={!hasChromaSet && !item.type}
              tagCategory={'cart-3'}
              value={'set'}
            >
              Set
            </TabsItem>
            <TabsItem
              className={styles['tab-item']}
              variant={'quaternary'}
              disabled={!hasChromaSet && !item.type}
              tagCategory={'box'}
              value={'bundle'}
            >
              Bundle
            </TabsItem>
          </Tabs>
        )}
      </div>
    );
  },
);

ItemCardWidget.Bundles = observer(({ itemId }: { itemId: string }) => {
  const shop = useStore()?.shop;
  const game = useStore().app.selectedGame;
  const item = itemId && shop?.getMappedCards.get(itemId);
  const hasBundles = item && 'bundles' in item;

  const dragScrollRef = useDragToScroll<HTMLDivElement>();

  //@TODO добавить обработку возвраста пета
  if (game === 'adopt' || !hasBundles) return;

  const router = useRouter();

  return (
    <div className={styles['bundles']}>
      <Typography className={styles['title']}>Bundles</Typography>
      <div ref={dragScrollRef} className={styles['bundles-list-wrapper']}>
        <div className={styles['bundles-list']}>
          {hasBundles &&
            Array.isArray(item?.bundles) &&
            item?.bundles.map((bundle: any) => {
              return (
                <CardOfferEntity
                  key={`${bundle.id}-bundle`}
                  onClick={() =>
                    router.push(`/shop/${bundle.category}/${bundle?.id}`)
                  }
                  topSlot={
                    <CardOfferEntityItem
                      isMM2Variant={game === 'mm2'}
                      tagsInfo={bundle.tagsInfo}
                      item={bundle.item}
                      //rarity={bundle.rarity}
                    />
                  }
                  bottomSlot={
                    <CardOfferEntityItemInfo
                      price={{
                        old: bundle.info.price.old,
                        current: bundle.info.price.current,
                      }}
                      title={bundle.info.title}
                    />
                  }
                />
              );
            })}
        </div>
      </div>
    </div>
  );
});

ItemCardWidget.Breadcrumbs = observer(({ item }: ItemCardWidgetItemProps) => {
  const pathname = usePathname();

  if (!item) return null;

  const categoryTitle = item.type
    ? ALL_FILTERS.find(filter => filter.filter === item.type)?.label
    : 'All';

  return (
    <div className={styles['breadcrumbs']}>
      <Breadcrumbs>
        <BreadcrumbsItem
          isActive={RegExp('^\\/[a-zA-Z]{2}$').test(pathname)}
          as={Link}
          href={'/'}
        >
          Market
        </BreadcrumbsItem>
        <BreadcrumbsItemSeparator />
        <BreadcrumbsItem
          isActive={RegExp('^\\/[a-zA-Z]{2}/categories$').test(pathname)}
          as={Link}
          href={'/categories'}
        >
          Catalog
        </BreadcrumbsItem>
        <BreadcrumbsItemSeparator />
        <BreadcrumbsItem
          isActive={RegExp('^\\/[a-zA-Z]{2}/shop$').test(pathname)}
          as={Link}
          href={`/shop?category=${item.type}`}
        >
          {categoryTitle ?? 'All'}
        </BreadcrumbsItem>
        <BreadcrumbsItemSeparator />
        <BreadcrumbsItem
          as={Link}
          isDisabled
          isActive={RegExp('^\\/([a-zA-Z]{2})\\/shop\\/.*$').test(pathname)}
          href={`/shop/${item.realName}`}
        >
          {item.name}
        </BreadcrumbsItem>
      </Breadcrumbs>
    </div>
  );
});

ItemCardWidget.Item = observer(({ item }: ItemCardWidgetBreadcrumbsProps) => {
  if (!item) return null;

  const game = useStore()?.app?.selectedGame;

  return (
    <div className={styles['item']}>
      <CardItemBlockEntity
        isMM2Variant={game === 'mm2'}
        src={item.imageUri}
        rare={item.rare}
        size={'l'}
      />
    </div>
  );
});

ItemCardWidget.Description = ({ realName }: ItemCardWidgetDescriptionProps) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, isLoading, error, isSuccess } = useDescription(realName ?? '');
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [isOpen, setIsOpen] = useState(false);

  if (isLoading) {
    return <Skeleton borderRadius={10} height={97} width={'100%'} />;
  }

  if ((!data?.desc && !isSuccess) || !data?.desc?.length) {
    return null;
  }

  return realName ? (
    <motion.div className={styles['description']}>
      <div className={styles['description-container']}>
        <Illustration
          style={{ color: '#6941C6' }}
          name={'document-text'}
          size={22}
          spriteName={'icons'}
        />
        <Typography className={styles['title']}>Description</Typography>
      </div>

      <div className={styles['expandable-container']}>
        <motion.div
          style={{
            height: isOpen ? 'max-content' : 58,
            cursor: 'pointer',
            overflow: 'hidden',
            maskImage: isOpen
              ? 'initial'
              : 'linear-gradient(to top, transparent, black 30%)',
          }}
          onTap={() => setIsOpen(!isOpen)}
          animate={{ height: isOpen ? 'max-content' : 58 }}
          transition={{
            duration: 0.35,
          }}
        >
          <Typography className={styles['text-full']}>{data?.desc}</Typography>
        </motion.div>
      </div>
    </motion.div>
  ) : null;
};
